import type { ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  CircleAlert,
  ListChecks,
  Play,
  Plug,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Undo2,
  Workflow,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ToolLaunchDialog } from '@/components/tools/ToolLaunchDialog';
import { ToolResultPageRenderer } from '@/components/tools/ToolResultPageRenderer';
import { ToolContinuitySummary } from '@/components/composer/ToolContinuitySummary';
import { evaluateToolReadiness } from '@shared/types/tool-readiness';
import {
  buildComposerPreviewComposition,
  materializeResultComposition,
} from '@shared/composer-preview';
import { designIntentSignature } from '@shared/tool-design-contract';
import { APP_OBJECT_KIND_LABELS } from '@shared/types/app-objects';
import { DesignBriefCard } from './DesignBriefCard';
import { type ForgePane, type ForgeStep, ForgeStepBar, ForgeSteps } from './ForgeSteps';
import type { ResultComposition } from '@shared/types/result-composition';
import {
  normalizeToolSurfaceSpec,
} from '@shared/types/tool-surface';
import type {
  FlowComposerDraft,
  FlowComposerResources,
} from '@shared/types/flow-composer';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';
import type {
  ProductDiagnosis,
  ProductDiagnosisAction,
} from '@shared/types/product-diagnosis';
import type { KnowledgeFolderWithCount } from '@/lib/knowledge';
import type { ToolResultTab } from '@/lib/tool-result-composition';
import type { ToolFieldDefinition } from '@shared/types/tool-manifest';
import { cn } from '@/lib/utils';

export interface ComposerSandboxTestState {
  status: FlowExecutionStatus | 'blocked';
  executionId: string | null;
  sandboxFlowId: string | null;
  missingInputs?: Array<{ key: string; label: string }>;
  diagnosis?: ProductDiagnosis | null;
  resultTab?: ToolResultTab | null;
  resultTabs?: ToolResultTab[];
  /** The test ran, but its result cannot be published as is (e.g. objects that name nothing). */
  publishBlocked?: string | null;
  /** How the test's objects will be found again in the list (L48, R2). */
  objectCoverage?: string | null;
}

interface ComposerPreviewProps {
  draft: FlowComposerDraft;
  resources: FlowComposerResources;
  /** The step or view chosen by the person; null follows the progression (the first step not done). */
  forgePane: ForgePane | null;
  availableFolders: KnowledgeFolderWithCount[];
  /** Folders the user has explicitly selected for this tool, in addition to those referenced by knowledge steps. */
  selectedKnowledgeFolderIds: string[];
  creating?: boolean;
  improving?: boolean;
  testing?: boolean;
  restoring?: boolean;
  canRestorePreviousDraft?: boolean;
  sandboxTest?: ComposerSandboxTestState | null;
  savingLayout?: boolean;
  confirmLabel?: string;
  onForgePaneChange: (pane: ForgePane) => void;
  onToggleKnowledgeFolder: (folderId: string, nextSelected: boolean) => void;
  onTest: () => void;
  /** Re-run the sandbox test with explicit values for inputs missing at test time. */
  onProvideTestInputs: (values: Record<string, unknown>) => void;
  /** Persist a presentation-only edit (labels/order) without a rerun. */
  onUiEdit: (draft: FlowComposerDraft) => void;
  onConfirm: () => void;
  onRestorePreviousDraft: () => void;
  onSandboxAction: (action: ProductDiagnosisAction) => void;
  /** Places a proposed message in the chat field for the user to edit and send. */
  onDraftMessage: (text: string) => void;
  /** « Valider la conception » for the current design sheet. */
  onValidateDesign: () => void;
  validatingDesign?: boolean;
  /** Usage sheet card (F1), shown instead of the legacy design sheet. */
  sheetSlot?: ReactNode;
  /** F6c: the published app's pages, changed here and only here (creator and managers). */
  appSlot?: ReactNode;
  /** The usage sheet changed since the tool was built: rebuild before testing. */
  sheetBlocksTest?: boolean;
  /** Validates the changed sheet and rebuilds the tool: shown next to « Tester » when the sheet blocks it. */
  onValidateSheet?: () => void;
  /** A turn is running (the rebuild included). */
  busy?: boolean;
  /** Why the last action on the tool (create, restore) was refused, shown next to the buttons. */
  actionError?: string | null;
  /** Example screen drawn from the usage sheet (F3), shown until a test result exists. */
  exampleSlot?: ReactNode;
  /** U1: the app filled with the test's objects, shown before the run's result once the test is done. */
  appPreview?: ReactNode;
}


type StepCategory =
  | 'Recherche internet'
  | 'Recherche CRM'
  | 'Email'
  | 'Calendrier'
  | 'Google Workspace'
  | 'Documentation'
  | 'Analyse IA'
  | 'Validation'
  | 'Historique'
  | 'Connexion'
  | 'Action';

interface StepRow {
  position: number;
  title: string;
  category: StepCategory;
  requiresValidation: boolean;
}

const fieldTypeLabel: Record<FlowComposerDraft['inputs'][number]['type'], string> = {
  email: 'Email',
  number: 'Nombre',
  select: 'Choix',
  text: 'Texte',
  textarea: 'Texte long',
  url: 'URL',
};

const buildTestFields = (draft: FlowComposerDraft): ToolFieldDefinition[] =>
  draft.inputs.map((input) => ({
    key: input.key,
    label: input.label,
    type:
      input.type === 'textarea' || input.type === 'select' || input.type === 'email'
        ? input.type
        : 'text',
    required: input.required,
    description: input.description,
    options: input.options,
  }));

const buildInitialTestValues = (draft: FlowComposerDraft): Record<string, string> =>
  Object.fromEntries(
    draft.inputs.map((input) => [
      input.key,
      input.defaultValue ?? input.options?.[0]?.value ?? '',
    ])
  );

const collectPlannerProposedFolderIds = (draft: FlowComposerDraft): Set<string> => {
  const ids = new Set<string>();
  for (const step of draft.dominos) {
    if (!step.actionType.startsWith('knowledge.')) continue;
    const value = step.config.folderId ?? step.config.folder_id;
    if (typeof value === 'string' && value.length > 0) ids.add(value);
  }
  return ids;
};

const getStepCategory = (actionType: string): StepCategory => {
  if (
    actionType.startsWith('exa.') ||
    actionType.startsWith('web.') ||
    actionType.includes('.exa')
  ) {
    return 'Recherche internet';
  }
  if (
    actionType.startsWith('crm.') ||
    actionType.includes('.crm') ||
    actionType.startsWith('account.')
  ) {
    return 'Recherche CRM';
  }
  if (actionType.startsWith('gmail.')) return 'Email';
  if (actionType.startsWith('gcalendar.') || actionType.includes('.calendar'))
    return 'Calendrier';
  if (actionType.startsWith('gdrive.') || actionType.startsWith('gsheet.'))
    return 'Google Workspace';
  if (actionType.startsWith('knowledge.')) return 'Documentation';
  if (actionType.startsWith('hitl.')) return 'Validation';
  if (actionType.includes('history') || actionType.includes('run_history'))
    return 'Historique';
  if (actionType.startsWith('integration.')) return 'Connexion';
  if (
    actionType.startsWith('llm.') ||
    actionType.startsWith('content.') ||
    actionType.startsWith('analysis.') ||
    actionType.startsWith('context.')
  ) {
    return 'Analyse IA';
  }
  return 'Action';
};

const buildStepRows = (
  draft: FlowComposerDraft,
  resources: FlowComposerResources
): StepRow[] =>
  draft.dominos.map((step, index) => {
    const position = index + 1;
    const primitive = resources.primitives.find((item) => item.step === position);
    const requiresValidation =
      step.hitlBehavior === 'always' ||
      (step.hitlBehavior !== 'never' && primitive?.risk === 'high_write');

    return {
      position,
      title: step.label,
      category: getStepCategory(step.actionType),
      requiresValidation,
    };
  });

export const ComposerPreview = ({
  draft,
  resources,
  forgePane,
  availableFolders,
  selectedKnowledgeFolderIds,
  creating = false,
  improving = false,
  testing = false,
  restoring = false,
  canRestorePreviousDraft = false,
  sandboxTest = null,
  savingLayout = false,
  confirmLabel = 'Créer le tool',
  onForgePaneChange,
  onToggleKnowledgeFolder,
  onTest,
  onValidateDesign,
  validatingDesign = false,
  sheetSlot,
  appSlot,
  sheetBlocksTest = false,
  onValidateSheet,
  busy = false,
  actionError = null,
  exampleSlot,
  appPreview,
  onProvideTestInputs,
  onUiEdit,
  onConfirm,
  onRestorePreviousDraft,
  onSandboxAction,
  onDraftMessage,
}: ComposerPreviewProps) => {
  const [testInputValues, setTestInputValues] = useState<Record<string, string>>({});
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  // Publish gate: a tool can only be created once a sandbox test passed for the
  // current draft. sandboxTest resets on any draft edit, so 'completed' here
  // always refers to the draft currently shown.
  const canPublish = sandboxTest?.status === 'completed' && !sandboxTest.publishBlocked;
  const readiness = evaluateToolReadiness(draft);
  const toolSurface = useMemo(
    () => normalizeToolSurfaceSpec(draft.toolSurface, { viewType: draft.viewType }),
    [draft.toolSurface, draft.viewType]
  );

  // UX-only layout editing (section/tab labels + order). While editing, the
  // preview renders from the working composition so changes show live; applying
  // persists via onUiEdit (no rerun — the data contract is unchanged).
  const [editingLayout, setEditingLayout] = useState(false);
  const [workingComposition, setWorkingComposition] = useState<ResultComposition | null>(
    null
  );
  const previewDraft = useMemo(
    () =>
      editingLayout && workingComposition
        ? { ...draft, resultComposition: workingComposition }
        : draft,
    [draft, editingLayout, workingComposition]
  );
  const illustratedTabs = useMemo(() => buildComposerPreviewComposition(previewDraft), [previewDraft]);
  const resultTabs = !editingLayout && sandboxTest?.resultTabs?.length ? sandboxTest.resultTabs : illustratedTabs.filter((tab) => tab.scope === 'run');
  const unconnectedTabs = !draft.readModel ? illustratedTabs.filter((tab) => tab.scope !== 'run') : [];
  const [testView, setTestView] = useState<'app' | 'result'>('app');
  const [activeResultTabId, setActiveResultTabId] = useState<string>(
    resultTabs[0]?.id ?? 'result'
  );
  const activeResultTab =
    resultTabs.find((tab) => tab.id === activeResultTabId) ?? resultTabs[0];
  const sandboxResultTab =
    !editingLayout && sandboxTest?.resultTabs?.length ? activeResultTab : sandboxTest?.status === 'completed' ? (sandboxTest.resultTab ?? null) : null;
  // U1: once the test is done, the app filled with its objects comes first.
  const showAppPreview = Boolean(appPreview) && Boolean(sandboxResultTab) && !editingLayout;
  // While editing the layout, always show the composition preview (not the run
  // result) so label/order edits are visible.
  const renderedResultTab = editingLayout
    ? activeResultTab
    : (sandboxResultTab ?? activeResultTab);

  const startEditingLayout = () => {
    setWorkingComposition(materializeResultComposition(draft));
    if (draft.readModel) return;
    setEditingLayout(true);
  };
  const cancelEditingLayout = () => {
    setEditingLayout(false);
    setWorkingComposition(null);
  };
  const applyLayout = () => {
    if (!workingComposition) return;
    onUiEdit({ ...draft, resultComposition: workingComposition });
    setEditingLayout(false);
    setWorkingComposition(null);
  };
  const updateTabLabel = (tabId: string, label: string) =>
    setWorkingComposition((current) =>
      current
        ? {
            ...current,
            tabs: current.tabs.map((tab) => (tab.id === tabId ? { ...tab, label } : tab)),
          }
        : current
    );
  const updateSectionLabel = (tabId: string, sectionId: string, label: string) =>
    setWorkingComposition((current) =>
      current
        ? {
            ...current,
            tabs: current.tabs.map((tab) =>
              tab.id === tabId
                ? {
                    ...tab,
                    sections: tab.sections.map((section) =>
                      section.id === sectionId ? { ...section, label } : section
                    ),
                  }
                : tab
            ),
          }
        : current
    );
  const moveSection = (tabId: string, index: number, direction: -1 | 1) =>
    setWorkingComposition((current) => {
      if (!current) return current;
      return {
        ...current,
        tabs: current.tabs.map((tab) => {
          if (tab.id !== tabId) return tab;
          const target = index + direction;
          if (target < 0 || target >= tab.sections.length) return tab;
          const sections = [...tab.sections];
          const [moved] = sections.splice(index, 1);
          sections.splice(target, 0, moved!);
          return { ...tab, sections };
        }),
      };
    });
  const stepRows = useMemo(() => buildStepRows(draft, resources), [draft, resources]);
  const proposedFolderIds = useMemo(
    () => collectPlannerProposedFolderIds(draft),
    [draft]
  );
  const selectedSet = useMemo(
    () => new Set(selectedKnowledgeFolderIds),
    [selectedKnowledgeFolderIds]
  );
  const failedChecks = readiness.checks.filter((check) => !check.passed);
  // A design with a full sheet must be validated, for this exact sheet, before a test.
  const hasDesignBrief = Boolean(draft.designIntent?.inputDetails);
  const designValidated = !sheetBlocksTest && (!hasDesignBrief
    || draft.designValidation?.signature === designIntentSignature(draft.designIntent));
  const testBlockedReason = designValidated ? null : sheetBlocksTest
    ? 'La fiche a changé : validez-la pour reconstruire l’outil, puis testez-le.'
    : 'Validez la fiche de conception avant de tester.';
  // 1 Fiche → 2 Test → 3 App: each step holds its own action.
  const stepDone: Record<ForgeStep, boolean> = { sheet: designValidated, test: canPublish, app: false };
  const currentStep: ForgeStep = !stepDone.sheet ? 'sheet' : !stepDone.test ? 'test' : 'app';
  const activePane: ForgePane = forgePane ?? currentStep;
  const testRunning = testing || sandboxTest?.status === 'pending' || sandboxTest?.status === 'running' || sandboxTest?.status === 'paused';
  const testFields = useMemo(() => buildTestFields(draft), [draft]);
  const testSubmitDisabled = testFields.some(
    (field) => field.required && !(testInputValues[field.key] ?? '').trim()
  );

  const openTest = () => {
    if (testFields.length === 0) {
      onTest();
      return;
    }
    setTestInputValues(buildInitialTestValues(draft));
    setTestDialogOpen(true);
  };

  const submitTest = () => {
    const values = Object.fromEntries(
      Object.entries(testInputValues).filter(([, value]) => value.trim().length > 0)
    );
    setTestDialogOpen(false);
    onProvideTestInputs(values);
  };

  useEffect(() => {
    if (resultTabs.some((tab) => tab.id === activeResultTabId)) return;
    setActiveResultTabId(resultTabs[0]?.id ?? 'result');
  }, [activeResultTabId, resultTabs]);

  return (
    <div className="flex min-h-full flex-col">
      <header className="border-b border-domino-cardBorder bg-domino-white/95">
        <div className="px-5 py-5 lg:px-8 lg:py-6">
          <div className="flex flex-wrap items-start justify-between gap-5">
            <div className="min-w-0 max-w-4xl">
              <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink lg:text-3xl">
                {draft.title}
              </h1>

            </div>

            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              {canRestorePreviousDraft && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onRestorePreviousDraft}
                  loading={restoring}
                  title="Revenir à la version précédente"
                  aria-label="Revenir à la version précédente"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
              )}

            </div>
          </div>

          <ForgeSteps active={activePane} current={currentStep} done={stepDone} onSelect={onForgePaneChange} />
        </div>

      </header>

      <main className="flex-1 px-5 py-5 lg:px-8 lg:py-7">
        {activePane === 'sheet' && (sheetBlocksTest ? (
          <ForgeStepBar tone="warning" text="La fiche a changé depuis la construction de l’outil : validez-la pour le reconstruire, puis testez-le.">
            {onValidateSheet && <Button size="sm" onClick={onValidateSheet} disabled={busy}>Valider la fiche et reconstruire</Button>}
          </ForgeStepBar>
        ) : designValidated ? (
          <ForgeStepBar tone="done" text="Fiche validée : l’outil est construit à partir d’elle.">
            <Button size="sm" variant="secondary" onClick={() => onForgePaneChange('test')}>Passer au test</Button>
          </ForgeStepBar>
        ) : <ForgeStepBar text="Validez la conception ci-dessous pour passer au test." />)}
        {activePane === 'test' && (() => {
          const bar: { tone: 'neutral' | 'done' | 'warning'; text: string } = !designValidated
            ? { tone: 'warning', text: testBlockedReason ?? '' }
            : testRunning ? { tone: 'neutral', text: 'Test en cours sur de vraies données…' }
            : canPublish ? { tone: 'done', text: ['Test réussi : l’outil peut être créé.', sandboxTest?.objectCoverage].filter(Boolean).join(' ') }
            : sandboxTest?.status === 'completed' && sandboxTest.publishBlocked ? { tone: 'warning', text: sandboxTest.publishBlocked }
            : sandboxTest?.status === 'failed' ? { tone: 'warning', text: 'Le dernier test n’a pas abouti : le diagnostic est ci-dessous.' }
            : sandboxTest?.status === 'blocked' ? { tone: 'warning', text: 'Il manque des informations pour lancer le test.' }
            : { tone: 'neutral', text: 'Lancez un test sur de vraies données : rien n’est écrit dans vos outils.' };
          return (
            <ForgeStepBar tone={bar.tone} text={bar.text}>
              <Button size="sm" variant={canPublish ? 'secondary' : 'primary'} onClick={openTest} loading={testing} disabled={!designValidated || testRunning}>
                <Play className="mr-2 h-4 w-4" />{sandboxTest ? 'Relancer le test' : 'Tester'}
              </Button>
              {canPublish && <Button size="sm" onClick={() => onForgePaneChange('app')}>Passer à l’app</Button>}
            </ForgeStepBar>
          );
        })()}
        {activePane === 'test' && !draft.readModel && !readiness.publishable && (
          <div className="mx-auto mb-5 flex max-w-7xl flex-wrap items-center gap-2 rounded-xl border border-domino-cardBorder bg-domino-white px-4 py-3 text-sm text-domino-inkSoft">
            <CircleAlert className="h-4 w-4 text-[var(--domino-warning)]" />
            {failedChecks.slice(0, 3).map((check) => (
              <span key={check.id}>{check.message}</span>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => onDraftMessage(
                `Le contrôle de qualité relève : ${failedChecks.map((check) => check.message).join(' ; ')}. Peux-tu proposer une correction ?`
              )}
              loading={improving}
            >
              Proposer une correction
            </Button>
          </div>
        )}

        {activePane === 'app' && (
          <>
            <ForgeStepBar
              tone={actionError ? 'error' : canPublish ? 'neutral' : 'warning'}
              text={actionError ?? (canPublish
                ? 'Le test a réussi : créez l’outil pour le retrouver dans Applications.'
                : !designValidated ? 'Validez d’abord la fiche, puis testez l’outil.'
                : sandboxTest?.publishBlocked ?? 'Un test réussi est nécessaire avant de créer l’outil.')}
            >
              <Button size="sm" onClick={onConfirm} loading={creating} disabled={!canPublish}>{confirmLabel}</Button>
            </ForgeStepBar>
            {appSlot ?? <AppStepSummary draft={draft} coverage={sandboxTest?.objectCoverage ?? null} />}
          </>
        )}
        {activePane === 'sheet' && sheetSlot && <div className="mx-auto max-w-7xl">{sheetSlot}</div>}
        {activePane === 'sheet' && !sheetSlot && hasDesignBrief && draft.designIntent && (
          <div className="px-5 pt-5 lg:px-8">
            <DesignBriefCard
              key={draft.designValidation?.signature ?? 'not-validated'}
              intent={draft.designIntent}
              validated={designValidated}
              validating={validatingDesign}
              onValidate={onValidateDesign}
              onEditLine={onDraftMessage}
            />
          </div>
        )}
        {activePane === 'test' && exampleSlot && !sandboxResultTab && !editingLayout && (
          <div className="mx-auto max-w-7xl px-5 pt-5 lg:px-8">{exampleSlot}</div>
        )}
        {activePane === 'test' && renderedResultTab && !(exampleSlot && !sandboxResultTab && !editingLayout) && (
          <div className="mx-auto max-w-7xl space-y-5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-domino-inkSoft">
                {sandboxResultTab ? 'Résultat du test' : 'Exemple de résultat'}
              </p>
              <div>
                {!editingLayout && !draft.readModel && (
                  <Button variant="ghost" size="sm" onClick={startEditingLayout}>
                    <SlidersHorizontal className="mr-2 h-4 w-4" />
                    Modifier l’affichage
                  </Button>
                )}
              </div>
            </div>

            {sandboxTest && (
              <div className="rounded-2xl border border-domino-cardBorder bg-domino-white p-4 shadow-sm">
                {sandboxTest.status === 'blocked' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CircleAlert className="h-4 w-4 text-[var(--domino-warning)]" />
                      <p className="text-sm font-semibold text-domino-ink">
                        À compléter pour tester
                      </p>
                    </div>
                    <Button
                      size="sm"
                      loading={testing}
                      onClick={openTest}
                    >
                      Compléter les informations
                    </Button>
                  </div>
                )}

                {(sandboxTest.status === 'pending' ||
                  sandboxTest.status === 'running' ||
                  sandboxTest.status === 'paused') && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-domino-aubergine" />
                      <p className="text-sm font-semibold text-domino-ink">
                        Test en cours
                      </p>
                    </div>
                    {sandboxTest.executionId && !draft.readModel && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          onSandboxAction({
                            type: 'open_run_details',
                            label: 'Voir le résultat du test',
                          })
                        }
                      >
                        Voir le résultat du test
                      </Button>
                    )}
                  </div>
                )}

                {sandboxTest.status === 'failed' && sandboxTest.diagnosis && (
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-domino-ink">
                        {sandboxTest.diagnosis.title}
                      </p>
                      <p className="mt-1 max-w-3xl text-sm leading-6 text-domino-inkSoft">
                        {sandboxTest.diagnosis.explanation}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          onSandboxAction(sandboxTest.diagnosis!.primaryAction)
                        }
                      >
                        {sandboxTest.diagnosis.primaryAction.label}
                      </Button>
                      {sandboxTest.diagnosis.secondaryActions.map((action) => (
                        <Button
                          key={`${action.type}:${action.label}`}
                          variant="secondary"
                          size="sm"
                          onClick={() => onSandboxAction(action)}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {sandboxTest.status === 'failed' && !sandboxTest.diagnosis && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-domino-inkSoft">
                      Le test n’a pas abouti pour le moment.
                    </p>
                    <Button variant="secondary" size="sm" onClick={openTest}>
                      Réessayer
                    </Button>
                  </div>
                )}

                {sandboxTest.status === 'completed' && (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-700" />
                      <p className="text-sm font-semibold text-domino-ink">
                        Test terminé
                      </p>
                    </div>
                    {sandboxTest.executionId && !draft.readModel && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          onSandboxAction({
                            type: 'open_run_details',
                            label: 'Voir le résultat du test',
                          })
                        }
                      >
                        Voir le résultat du test
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {showAppPreview && (
              <div className="flex flex-wrap gap-1 border-b border-domino-cardBorder" role="tablist" aria-label="Ce que le test montre">
                {([['app', 'L’app'], ['result', 'Résultat du lancement']] as const).map(([view, label]) => (
                  <button key={view} type="button" role="tab" aria-selected={testView === view} onClick={() => setTestView(view)}
                    className={cn('border-b-2 px-3 py-2 text-sm font-semibold transition', testView === view
                      ? 'border-domino-coral text-domino-ink' : 'border-transparent text-domino-inkSoft hover:text-domino-ink')}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {showAppPreview && testView === 'app' ? appPreview : <>
            {resultTabs.length > 1 && (
              <div
                className="flex flex-wrap gap-1 border-b border-domino-cardBorder"
                role="tablist"
              >
                {resultTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={tab.id === activeResultTabId}
                    className={cn(
                      'flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-semibold transition',
                      tab.id === activeResultTabId
                        ? 'border-domino-coral text-domino-ink'
                        : 'border-transparent text-domino-inkSoft hover:text-domino-ink'
                    )}
                    onClick={() => setActiveResultTabId(tab.id)}
                  >
                    {tab.label}

                  </button>
                ))}
              </div>
            )}

            {editingLayout && workingComposition && (
              <div className="space-y-4 rounded-2xl border border-domino-aubergine/30 bg-domino-lilac/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="h-4 w-4 text-domino-aubergine" />
                    <p className="text-sm font-semibold text-domino-ink">
                      Personnaliser l'affichage
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={cancelEditingLayout}>
                      Annuler
                    </Button>
                    <Button size="sm" loading={savingLayout} onClick={applyLayout}>
                      Appliquer
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-domino-inkSoft">
                  Renomme et réordonne les sections. Aucune donnée n'est recalculée.
                </p>
                {workingComposition.tabs.map((tab) => (
                  <div
                    key={tab.id}
                    className="space-y-2 rounded-xl border border-domino-cardBorder bg-domino-white p-3"
                  >
                    {workingComposition.tabs.length > 1 && (
                      <Input
                        value={tab.label}
                        placeholder="Nom de l'onglet"
                        onChange={(event) => updateTabLabel(tab.id, event.target.value)}
                      />
                    )}
                    {tab.sections.map((section, index) => (
                      <div key={section.id} className="flex items-center gap-2">
                        <Input
                          className="flex-1"
                          value={section.label}
                          placeholder="Nom de la section"
                          onChange={(event) =>
                            updateSectionLabel(tab.id, section.id, event.target.value)
                          }
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Monter la section"
                          disabled={index === 0}
                          onClick={() => moveSection(tab.id, index, -1)}
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Descendre la section"
                          disabled={index === tab.sections.length - 1}
                          onClick={() => moveSection(tab.id, index, 1)}
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <ToolResultPageRenderer
              composition={renderedResultTab.composition}
              viewEnvelopesByInstanceId={renderedResultTab.viewEnvelopesByInstanceId}
              pageMode={toolSurface.result.pageMode}
              pageTitle={renderedResultTab.label}
            />
            </>}
          </div>
        )}

        {activePane === 'test' && !renderedResultTab && !exampleSlot && <p className="text-sm text-domino-inkSoft">Raccordez les sources dans la définition pour obtenir un aperçu.</p>}
        {activePane === 'settings' && (
          <div className="mx-auto max-w-4xl space-y-8">
            {unconnectedTabs.length > 0 && <section className="border-l-2 border-amber-400 pl-4"><h2 className="text-sm font-semibold text-domino-ink">Vues à raccorder</h2><p className="mt-2 text-sm leading-6 text-domino-inkSoft">{unconnectedTabs.map((tab) => tab.label).join(' · ')}. Ces vues demandent des données issues de plusieurs utilisations. Leur affichage doit être vérifié avec des sources réelles lors du test.</p></section>}
            {!draft.designIntent && draft.description && <p className="text-sm leading-6 text-domino-inkSoft">{draft.description}</p>}
              <section className="rounded-2xl border border-domino-cardBorder bg-domino-white p-5 shadow-[var(--domino-shadow-soft)]">
                <p className="text-sm font-semibold text-domino-ink">Sources documentaires</p>
                <div className="mt-4 grid gap-3 2xl:grid-cols-2">
                  {availableFolders.map((folder) => {
                    const isProposed = proposedFolderIds.has(folder.id);
                    const isSelected = isProposed || selectedSet.has(folder.id);
                    return (
                      <div
                        key={folder.id}
                        className="rounded-xl border border-domino-cardBorder bg-domino-warm p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-domino-ink">{folder.name}</p>
                            <p className="mt-1 text-xs text-domino-inkSoft">
                              {folder.document_count} document
                              {folder.document_count > 1 ? 's' : ''}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant={isSelected ? 'primary' : 'secondary'}
                            size="sm"
                            disabled={isProposed}
                            onClick={() =>
                              onToggleKnowledgeFolder(folder.id, !isSelected)
                            }
                          >
                            {isProposed ? 'Utilisé par l’application' : isSelected ? 'Retirer' : 'Ajouter'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {availableFolders.length === 0 && (
                    <div className="rounded-xl border border-dashed border-domino-cardBorder bg-domino-warm p-5 text-sm text-domino-inkSoft">
                      Aucun dossier disponible.
                    </div>
                  )}
                </div>
              </section>
            {draft.designIntent && !hasDesignBrief && (
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-domino-ink">Usage prévu</h2>
                <p className="text-sm text-domino-ink">{draft.designIntent.goal}</p>
                <p className="text-sm text-domino-inkSoft">{draft.designIntent.value}</p>
                <p className="text-xs text-domino-inkSoft">{draft.designIntent.scope}</p>
                <dl className="space-y-3 text-sm">
                  {[
                    ['Informations de départ', draft.designIntent.inputs],
                    ['Objets concernés', draft.designIntent.entities],
                    ['Relations', draft.designIntent.relationships],
                    ['Résultat attendu', draft.designIntent.outputs],
                    ['Critères de réussite', draft.designIntent.successCriteria],
                  ].map(([label, values]) => Array.isArray(values) && values.length > 0 ? (
                    <div key={String(label)}>
                      <dt className="font-medium text-domino-ink">{String(label)}</dt>
                      <dd className="mt-1 text-domino-inkSoft">{values.join(' · ')}</dd>
                    </div>
                  ) : null)}
                </dl>
              </section>
            )}
            <ToolContinuitySummary
              continuationCapability={draft.continuationCapability}
              continuityContract={draft.continuityContract}
            />
            <section>
              <div className="flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-domino-aubergine" />
                <h2 className="text-sm font-semibold text-domino-ink">À renseigner</h2>
              </div>
              <div className="mt-3 divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
                {draft.inputs.length === 0 ? (
                  <p className="py-3 text-sm text-domino-inkSoft">Rien à renseigner.</p>
                ) : (
                  draft.inputs.map((input) => (
                    <div
                      key={input.key}
                      className="flex items-center justify-between gap-4 py-3"
                    >
                      <p className="font-medium text-domino-ink">{input.label}</p>
                      <p className="shrink-0 text-xs text-domino-inkSoft">
                        {input.required ? 'Requis · ' : 'Optionnel · '}
                        {fieldTypeLabel[input.type]}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-2">
                <Workflow className="h-4 w-4 text-domino-aubergine" />
                <h2 className="text-sm font-semibold text-domino-ink">Enchaînement</h2>
              </div>
              <ol className="mt-3 divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
                {stepRows.map((step) => (
                  <li
                    key={`${step.position}-${step.title}`}
                    className="flex items-start gap-4 py-3"
                  >
                    <span className="w-5 shrink-0 pt-0.5 text-xs tabular-nums text-domino-inkSoft">
                      {String(step.position).padStart(2, '0')}
                    </span>
                    <p className="min-w-0 flex-1 font-medium text-domino-ink">
                      {step.title}
                    </p>
                    <p className="shrink-0 text-xs text-domino-inkSoft">
                      {step.category}
                      {step.requiresValidation ? ' · Validation' : ''}
                    </p>
                  </li>
                ))}
              </ol>
            </section>

            {(resources.connections.length > 0 || resources.contexts.length > 0) && (
              <section>
                <div className="flex items-center gap-2">
                  <Plug className="h-4 w-4 text-domino-aubergine" />
                  <h2 className="text-sm font-semibold text-domino-ink">Sources</h2>
                </div>
                <div className="mt-3 divide-y divide-domino-cardBorder border-y border-domino-cardBorder">
                  {resources.connections.map((connection) => (
                    <div key={connection.key} className="flex items-center justify-between gap-3 py-3">
                      <p className="min-w-0 truncate font-medium text-domino-ink">{connection.label}</p>
                      <p className="shrink-0 text-xs text-domino-inkSoft">
                        {connection.connected ? 'Connecté' : 'À connecter'}
                      </p>
                    </div>
                  ))}

                  {resources.contexts.map((context) => (
                    <div key={context.key} className="flex items-center justify-between gap-3 py-3">
                      <p className="min-w-0 truncate font-medium text-domino-ink">{context.label}</p>
                      <p className="shrink-0 text-xs text-domino-inkSoft">
                        {context.activeEntries} entrée{context.activeEntries > 1 ? 's' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <ToolLaunchDialog
        open={testDialogOpen}
        toolTitle={draft.title}
        composerTitle="Tester l’outil"
        submitLabel="Lancer le test"
        fields={testFields}
        values={testInputValues}
        missingRequirement={null}
        hasActiveRun={false}
        runtimeLoading={false}
        runtimeSubmitting={testing}
        submitDisabled={testSubmitDisabled}
        onClose={() => setTestDialogOpen(false)}
        onSubmit={submitTest}
        onRefresh={() => undefined}
        onValueChange={(key, value) =>
          setTestInputValues((current) => ({ ...current, [key]: value }))
        }
      />
    </div>
  );
};

/** What step 3 creates, when the app has no pages yet: the list it keeps, how it finds each object again. */
const AppStepSummary = ({ draft, coverage }: { draft: FlowComposerDraft; coverage: string | null }) => {
  const data = draft.appData;
  return (
    <div className="mx-auto max-w-3xl space-y-2 rounded-xl border border-domino-cardBorder bg-domino-white px-5 py-4 text-sm leading-6 text-domino-ink">
      <p className="font-semibold">Ce que l’app gardera</p>
      {data ? (
        <p>
          La liste « {data.title} » :{' '}
          {data.mode === 'rows'
            ? `chaque objet (${APP_OBJECT_KIND_LABELS[data.object ?? 'other']}) est retrouvé d’un lancement à l’autre`
            : 'un résultat par lancement'}
          {data.statuses.length > 0 ? `, avec les statuts ${data.statuses.map((status) => `« ${status.label} »`).join(', ')}.` : '.'}
        </p>
      ) : <p>Chaque lancement donne un résultat, retrouvé dans l’historique de l’outil.</p>}
      {coverage && <p className="text-domino-inkSoft">{coverage}</p>}
      <p className="text-domino-inkSoft">Une fois créé, l’outil se trouve dans Applications : l’équipe le lance et travaille sa liste.</p>
    </div>
  );
};

